import React from 'react';
import {StyleSheet, View, Pressable, ActivityIndicator} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import {FontSizes} from '@/Utils/Dimensions';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import PlayerAvatar from '@/Screens/Leaderboard/components/PlayerAvatar';
import {
  lbColors,
  lbRadius,
  avatarColorForUser,
} from '@/Screens/Leaderboard/leaderboardTheme';
import {initialsForName} from '@/Screens/Leaderboard/useLeaderboardData';
import type {ArenaPlayerGame} from '../hooks/useGetArenaPlayerGames';

// ── ArenaPlayerGames ─────────────────────────────────────────────────────────
// A player's full arena game history (from GET /arenaPlayerGames), themed to the
// wins-leaderboard look. Presentational only — the caller owns the fetch (the
// useGetArenaPlayerGames hook) and the row-open handler (PlayChess's
// openArenaGameByTable, threaded through as onOpenGame). Newest game first.
// Tapping a game opens its replay (redis-first, DB-uuid fallback) unless
// `disabled` (the viewer is mid-game and must not jump off their live board).

const FIRE_COLOR = '#FF6B00';

const OUTCOME: Record<string, {label: string; bg: string; ink: string}> = {
  win: {label: 'Win', bg: lbColors.greenBg, ink: lbColors.greenInk},
  draw: {label: 'Draw', bg: '#EEF2F5', ink: '#5B7A99'},
  loss: {label: 'Loss', bg: '#FBEAE3', ink: lbColors.red},
};

const ArenaGameRow: React.FC<{
  game: ArenaPlayerGame;
  disabled?: boolean;
  onOpenGame: (game: ArenaPlayerGame) => void;
}> = ({game, disabled, onOpenGame}) => {
  const oc = OUTCOME[game.outcome] || OUTCOME.draw;
  return (
    <Pressable
      onPress={() => !disabled && onOpenGame(game)}
      disabled={disabled}
      style={({pressed}: any) => [
        styles.gameRow,
        pressed && !disabled ? {backgroundColor: lbColors.rowBg} : null,
      ]}
      data-testid="arena-game-row">
      <PlayerAvatar
        initials={initialsForName(game.oppName)}
        color={avatarColorForUser(game.oppId)}
        size={34}
      />
      <Box style={styles.mid}>
        <Box flexDirection="row" alignItems="center" gap={6}>
          <CCText
            fontSize={FontSizes[13]}
            fontWeight="semibold"
            numberOfLines={1}
            style={{color: lbColors.ink, flexShrink: 1}}>
            {game.oppName}
          </CCText>
          {Boolean(game.oppRating) && (
            <CCText fontSize={FontSizes[11]} style={{color: lbColors.muted}}>
              {game.oppRating}
            </CCText>
          )}
          {game.berserk && (
            <MaterialCommunityIcons name="flash" size={13} color={FIRE_COLOR} />
          )}
        </Box>
        <CCText fontSize={FontSizes[10]} style={{color: lbColors.muted}}>
          {game.color === 'white' ? 'White' : 'Black'} · {game.moveCount} moves
          {game.endType ? ` · ${game.endType}` : ''}
        </CCText>
      </Box>
      <View style={[styles.outcomePill, {backgroundColor: oc.bg}]}>
        <CCText
          fontSize={FontSizes[11]}
          fontWeight="bold"
          style={{color: oc.ink}}>
          {oc.label}
        </CCText>
      </View>
      <CCText
        fontSize={FontSizes[14]}
        fontWeight="bold"
        style={{color: lbColors.ink, width: 30, textAlign: 'right'}}>
        {game.points}
      </CCText>
      {!disabled && (
        <MaterialCommunityIcons
          name="chevron-right"
          size={18}
          color={lbColors.faint}
        />
      )}
    </Pressable>
  );
};

const ArenaPlayerGames: React.FC<{
  games: ArenaPlayerGame[];
  loading: boolean;
  disabled?: boolean;
  emptyText?: string;
  onOpenGame: (game: ArenaPlayerGame) => void;
}> = ({games, loading, disabled, emptyText, onOpenGame}) => {
  if (loading && !games.length) {
    return (
      <Box style={styles.panel}>
        <ActivityIndicator color={lbColors.goldDeep} />
      </Box>
    );
  }
  if (!games.length) {
    return (
      <Box style={styles.panel}>
        <CCText fontSize={FontSizes[12]} style={{color: lbColors.muted}}>
          {emptyText || 'No games played yet.'}
        </CCText>
      </Box>
    );
  }
  // Newest game first for history readability (server returns oldest→newest).
  const ordered = [...games].reverse();
  return (
    <Box style={styles.panel} data-testid="arena-player-games">
      {ordered.map(g => (
        <ArenaGameRow
          key={g.seq}
          game={g}
          disabled={disabled}
          onOpenGame={onOpenGame}
        />
      ))}
    </Box>
  );
};

const styles = StyleSheet.create({
  panel: {
    backgroundColor: lbColors.rowBg,
    borderRadius: lbRadius.md,
    paddingHorizontal: 6,
    paddingVertical: 4,
    marginTop: 6,
    marginBottom: 4,
  },
  gameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: lbRadius.sm,
  },
  mid: {
    flex: 1,
    minWidth: 0,
  },
  outcomePill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: lbRadius.pill,
  },
});

export default ArenaPlayerGames;
